import React, { useEffect, useState } from "react";
import { Button, Col, Grid, Row } from "react-bootstrap";
import "./../shows.scss";
import "./show_campaign_ad_settings_modal.scss";
import { FormStep, FormStepWithNav } from "../../../forms/form_steps/form_steps";
import findLast from "lodash/findLast";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import range from "lodash/range";
import mapValues from "lodash/mapValues";

import { connect, useDispatch, useSelector } from "react-redux";
import ExternalLink from "../../../lib/external_link";
import { FieldArray, reduxForm, submit } from "redux-form";
import { updateShow } from "../../../../action_managers/shows";
import LoadingButton from "../../../forms/loading_button/loading_button";
import { showWarning } from "../../../../actions/app";
import { validateCPM, validateLink, validatePromoCode } from "./ad_settings_validation";
import BaseModalV2 from "../../../modals/base_modal_v2";
import { CPMForm } from "./cpm_form";
import { PromoCodeForm } from "./promo_code_form";
import { SocialMediaLinksForm } from "./social_media_links_form";
import { HostsForm } from "./host_names_form";
import Divider from "../../../lib/divider";
import { AD_SETTINGS_FORM_NAME } from "./constants";

const audienceSizeToSuggestedCPMs = {
  0: [1000, 1200],
  100: [1000, 1200],
  1000: [1200, 1500],
  5000: [1500, 2000],
  10000: [2000, 2500],
  25000: [2500, 3000],
};

const ModalFooter = props => {
  const {
    step,
    setStep,
    close,
    isCreateForm,
    defaultCPMByType,
    isFetchingValidation,
    isInvalidPromoCode,
  } = props;

  let isLoading = useSelector(state => get(state, ["showForm", "isLoading"]));
  const formValues = useSelector(state => get(state, ["form", AD_SETTINGS_FORM_NAME, "values"]));

  const dispatch = useDispatch();
  const saveForm = () => {
    dispatch(submit(AD_SETTINGS_FORM_NAME));
  };

  /*
    validate fields
   */
  let disableNextButton = false;

  // validate CPMs
  if (step === 1) {
    const sectionEnabledKeys = {
      preRecorded: "isPreRecordedEnabled",
      hostRead: "isHostReadEnabled",
    };

    ["preRecorded", "hostRead"].forEach(type => {
      [
        ["PreRollCPM", "discount"],
        ["MidRollCPM", "default"],
        ["PostRollCPM", "discount"],
      ].forEach(([position, cpmType]) => {
        const cpm = get(formValues, type + position, 0.0);
        const defaultCPM = defaultCPMByType[type][cpmType];
        const [disableNext] = validateCPM(cpm, defaultCPM);
        const isAdTypeEnabled = get(formValues, sectionEnabledKeys[type], false);
        disableNextButton |= disableNext && isAdTypeEnabled;
      });
    });
  }

  // validate promoCode
  if (step === 2) {
    disableNextButton = isInvalidPromoCode || isFetchingValidation;
  }

  // validate socialLinks
  if (step === 4) {
    ["facebook", "instagram", "twitter"].forEach(site => {
      disableNextButton |= validateLink(get(formValues, `${site}Link`), `${site}.com`)[0];
    });
  }

  let nextOrSaveBtn;
  if (isCreateForm) {
    nextOrSaveBtn = (
      <Button
        className="btn btn-primary width-100 margin-top--large"
        onClick={() => setStep(step + 1)}
        disabled={!!disableNextButton}>
        Next
      </Button>
    );
  } else {
    nextOrSaveBtn = (
      <LoadingButton
        isLoading={isLoading}
        className="btn btn-primary width-100 margin-top--large"
        onClick={saveForm}
        disabled={disableNextButton}>
        Save
      </LoadingButton>
    );
  }

  let backOrCloseBtn;
  if (isCreateForm && step !== 1) {
    backOrCloseBtn = (
      <Button
        className="btn btn-secondary width-100 margin-top--large margin-right--large"
        onClick={() => setStep(step - 1)}>
        Back
      </Button>
    );
  } else {
    backOrCloseBtn = (
      <Button
        className="btn btn-secondary width-100 margin-top--large margin-right--large"
        onClick={close}>
        Close
      </Button>
    );
  }

  return (
    <>
      <FormStep step={range(1, 5)} currentStep={step}>
        <Col xs={6} md={5} mdOffset={1}>
          {backOrCloseBtn}
        </Col>
        <Col xs={6} md={5}>
          {nextOrSaveBtn}
        </Col>
      </FormStep>
      {isCreateForm && (
        <FormStep step={5} currentStep={step}>
          <Col xs={6} md={5} mdOffset={1}>
            {backOrCloseBtn}
          </Col>
          <Col xs={6} md={5}>
            <LoadingButton
              isLoading={isLoading}
              onClick={saveForm}
              className="btn btn-primary width-100 margin-top--large">
              Save & Agree
            </LoadingButton>
          </Col>
        </FormStep>
      )}
    </>
  );
};

const ShowCampaignAdSettingsModal = props => {
  const { close, onSubmit, isCreateForm, defaultCPMByType, show } = props;
  const showUUID = get(show, "uuid");
  const [step, setStep] = useState(1);

  const promoCode = useSelector(state =>
    get(state, ["form", AD_SETTINGS_FORM_NAME, "values", "promoCode"], "")
  );

  const [isFetchingValidation, setIsFetchingValidation] = useState(false);
  const [promoCodeWarning, setPromoCodeWarning] = useState("");

  // promoCode validation is handled at top level since it makes an API call (don't want duplicate calls)
  useEffect(() => {
    async function validate() {
      setIsFetchingValidation(true);
      const [, msg] = await validatePromoCode(showUUID, promoCode);
      setPromoCodeWarning(msg);
      setIsFetchingValidation(false);
    }
    validate();
  }, [promoCode, promoCodeWarning, showUUID]);

  return (
    <BaseModalV2
      key="advertising-setup"
      isLoading={false}
      close={close}
      columns={8}
      offset={3}
      showContent={true}
      hideTitleDivider={true}
      Content={
        <Grid fluid className="ad-settings-modal-content">
          <form onSubmit={onSubmit}>
            <Row>
              <Col xs={12} md={8} mdOffset={3} className={"margin-top--large"}>
                <div className={"margin-top--large"}>
                  <h2>Advertising Setup</h2>
                  <Divider smallMargin className="seedcast-modal__title-divider" />
                </div>
              </Col>
              <FormStepWithNav
                hideMenuXS={isCreateForm}
                setStep={setStep}
                currentStep={step}
                disableClickNav={isCreateForm}>
                <FormStep step={1} currentStep={step} navName="CPM">
                  <CPMForm defaultCPMByType={defaultCPMByType} />
                </FormStep>
                <FormStep step={2} currentStep={step} navName="Promo Code">
                  <PromoCodeForm
                    warningMsg={promoCodeWarning}
                    isFetchingValidation={isFetchingValidation}
                  />
                </FormStep>
                <FormStep step={3} currentStep={step} navName="Hosts">
                  <FieldArray name="hosts" component={HostsForm} />
                </FormStep>
                <FormStep step={4} currentStep={step} navName="Social Media">
                  <SocialMediaLinksForm />
                </FormStep>

                {isCreateForm && (
                  <FormStep step={5} currentStep={step} navName="Finish">
                    <div>
                      You’re all set! Just one more thing before the advertising opportunities can
                      flow in.
                    </div>

                    <div className="margin-top--medium margin-bottom--medium bold">
                      By opting into RedCircle’s Ad Marketplace, you’re agreeing that we can:
                    </div>
                    <div>
                      <ul>
                        <li>
                          Share information about your show with advertisers including audience
                          size, category, CPM, etc.{" "}
                        </li>
                      </ul>
                    </div>
                    <div className={"margin-top--medium"}>
                      <ul>
                        <li>
                          Automatically create default audio blocks where approved ads can be
                          inserted into your back catalog. Don’t worry, we’ll never play ads you
                          haven’t approved!
                        </li>
                      </ul>
                    </div>

                    <ExternalLink
                      src="http://support.redcircle.com/en/articles/3591720-advertising"
                      className="bold pointer">
                      Learn more about advertising in RedCircle
                    </ExternalLink>
                  </FormStep>
                )}
              </FormStepWithNav>
            </Row>
          </form>
        </Grid>
      }
      Footer={
        <ModalFooter
          step={step}
          setStep={setStep}
          close={close}
          isCreateForm={isCreateForm}
          defaultCPMByType={defaultCPMByType}
          showUUID={showUUID}
          isInvalidPromoCode={!isEmpty(promoCodeWarning)}
          isFetchingValidation={isFetchingValidation}
        />
      }
    />
  );
};

const onSubmit = (values, dispatch, props) => {
  const { show, close } = props;
  const showUUID = get(show, "uuid");
  const hasSetupAdvertising = get(show, "advertisementSettings");

  delete values["showUUID"];
  const isPreRecorded = get(values, "isPreRecordedEnabled", false);
  const isHostRead = get(values, "isHostReadEnabled", false);
  const promoCode = get(values, "promoCode");

  if (!isPreRecorded) {
    delete values["preRecordedPreRollCPM"];
    delete values["preRecordedMidRollCPM"];
    delete values["preRecordedPostRollCPM"];
  }
  if (!isHostRead) {
    delete values["hostReadPreRollCPM"];
    delete values["hostReadMidRollCPM"];
    delete values["hostReadPostRollCPM"];
  }

  values = mapValues(values, (val, key) => {
    return val === "" ? null : val;
  });

  dispatch(updateShow({ uuid: showUUID, promoCode, advertisementSettings: { ...values } })).then(
    resp => {
      const statusCode = get(resp, "status");

      if (statusCode !== 200) {
        dispatch(
          showWarning(
            "Something went wrong saving your advertising settings. Check your configuration and try again."
          )
        );
      }

      // only close the modal on the first setup
      if (statusCode === 200 && !hasSetupAdvertising) {
        close();
      }
    }
  );
};

const getDefaultCPMsForAudienceSize = size => {
  return findLast(audienceSizeToSuggestedCPMs, (_, audienceSize) => {
    return size >= audienceSize;
  });
};

const AdSettingsModalForm = reduxForm({
  onSubmit,
  form: AD_SETTINGS_FORM_NAME,
})(ShowCampaignAdSettingsModal);

export default connect((state, ownProps) => {
  const user = state.user.user;
  const firstName = get(user, "firstName");
  const lastName = get(user, "lastName");

  const adSettings = get(ownProps, ["show", "advertisementSettings"], {}) || {};
  let audienceSize = get(ownProps, ["show", "estimatedWeeklyDownloads"], 0);

  let [defaultPreRecordedCPM, defaultHostReadCPM] = getDefaultCPMsForAudienceSize(audienceSize);
  const roundedDiscount = price => Math.round((price * 0.75) / 100) * 100;
  const defaultHostReadCPMWithDiscount = roundedDiscount(defaultHostReadCPM);
  const defaultPreRecordedCPMWithDiscount = roundedDiscount(defaultPreRecordedCPM);

  const defaultCPMByType = {
    preRecorded: {
      default: defaultPreRecordedCPM,
      discount: defaultPreRecordedCPMWithDiscount,
    },
    hostRead: {
      default: defaultHostReadCPM,
      discount: defaultHostReadCPMWithDiscount,
    },
  };

  const promoCode = get(ownProps, ["show", "promoCode"]);
  // We don't want to show the finish agreement section if the podcaster has already setup their
  // settings. Also, we change "Next" button to "Submit" since they don't have to go through
  // the entire flow to update a field on updates.
  const isCreateForm = isEmpty(adSettings);

  if (!adSettings?.hosts) {
    adSettings.hosts = [{ fullName: { firstName, lastName } }];
  }
  return {
    adSettings,
    audienceSize,
    defaultCPMByType,
    isCreateForm,
    initialValues: {
      promoCode,
      hostNames: [{ firstName, lastName }],
      isHostReadEnabled: true,
      isPreRecordedEnabled: true,
      hostReadPreRollCPM: roundedDiscount(defaultHostReadCPM),
      hostReadMidRollCPM: defaultHostReadCPM,
      hostReadPostRollCPM: roundedDiscount(defaultHostReadCPM),
      preRecordedPreRollCPM: roundedDiscount(defaultPreRecordedCPM),
      preRecordedMidRollCPM: defaultPreRecordedCPM,
      preRecordedPostRollCPM: roundedDiscount(defaultPreRecordedCPM),
      ...adSettings,
    },
  };
})(AdSettingsModalForm);
